import { nsMsxsl } from '@qdabra/forms-shared/xmlNamespaces';
import qdDateFormatter from './Qd/formsviewer/qdDateFormatter';
import { getXmlDate, getXmlTime, makeIsoDate, parseIsoDate } from './Qd/utility/date.helpers';
import { getNodeData } from './Qd/utility/nodeDataHelpers';
import { makeTextNode } from './Qd/utility/xmlHelpers';
import { getNodeValue, ownerDocument } from "./Qd/xmlUtility";
import { FunctionResolver, Utilities, XBoolean, XNodeSet, XNumber, XPathContext, XPathParser, XString, argError, emptyXString, falseXBoolean, getParam, getParams } from './Qd/xpathFunctions/xpath.function.helpers';
import makeFvXsltFunctions, { namespaceFv } from './Qd/xpathFunctions/xpath.functions.fvxslt';
import currencyLocales from './currencyLocales';

const dateFormatter = qdDateFormatter();

function inherit(targetType, superType) {
    targetType.prototype = new superType();
    targetType.prototype.constructor = superType;
    targetType.superClass = superType;
}

function XPathFunctionBase() {

}

inherit(XPathFunctionBase, FunctionResolver);


const makeSingleValueNodeSet = function (baseNode, val) {
    const textNode = makeTextNode(baseNode, val);
    const nodeSet = new XNodeSet();

    nodeSet.add(textNode);

    return nodeSet;
};


// ===========
// FVFunctions
// ===========

function FVFunctions(dataSources, userName, characterLimitSource, viewManager, settings) {
    const namespaceXdDate = "http://schemas.microsoft.com/office/infopath/2003/xslt/Date";
    const namespaceXdMath = "http://schemas.microsoft.com/office/infopath/2003/xslt/Math";
    const namespaceXdXDocument = "http://schemas.microsoft.com/office/infopath/2003/xslt/xDocument";
    const namespaceXdUser = "http://schemas.microsoft.com/office/infopath/2006/xslt/User";
    const namespaceXdFormatting = "http://schemas.microsoft.com/office/infopath/2003/xslt/formatting";
    const namespaceXdUtil = "http://schemas.microsoft.com/office/infopath/2003/xslt/Util";
    const namespaceXdEnvironment = 'http://schemas.microsoft.com/office/infopath/2006/xslt/environment';
    const namespaceIpApp = 'http://schemas.microsoft.com/office/infopath/2006/XPathExtension/ipApp';

    const self = this;

    function makeElementNode(node, name, val) {
        var owner = ownerDocument(node), newNode;

        newNode = owner.createElement(name);

        if (val) {
            newNode.textContent = val;
        }

        return newNode;
    }

    function nz(c, expr) {
        var ns = expr ? expr.evaluate(c) : null,
            val,
            retval,
            narray, i;

        // Nz requires a node-set as argument
        if (arguments.length !== 2 || !Utilities.instance_of(ns, XNodeSet)) {
            argError("Nz", "node-set");
        }

        narray = ns.toUnsortedArray().map(function (n) {
            return getNodeValue(n) === "" ? makeTextNode(n, "0") : n;
        });

        retval = new XNodeSet();
        retval.addArray(narray);
        return retval;
    }

    function xdEval(c) {
        var params = getParams(c, arguments),
            parser = new XPathParser(),
            parsed = parser.parse(params[1]),
            sourceNodes = params[0].toArray(),
            parent = makeElementNode(c.contextNode, "item"),
            retval = new XNodeSet(),
            resultNodes = sourceNodes.map(function (node) {
                var execContext = new XPathContext(c.variableResolver, c.namespaceResolver, c.functionResolver);
                execContext.expressionContextNode = node;

                var val = parsed.evaluate(execContext).stringValue();
                var newNode = makeElementNode(c.contextNode, "item", val);

                parent.appendChild(newNode);
                return newNode;
            });

        retval.addArray(resultNodes);
        return retval;
    }

    // Determined empirically: avg, max, min ignore blank values and return blank if
    // all are blank or no nodes

    function getNonBlankNumbers(nodeArray) {
        var strings = nodeArray.map(function (n) { return XNodeSet.prototype.stringForNode(n); });
        var nonBlank = strings.filter(function (s) { return s !== ""; });
        var numbers = nonBlank.map(function (s) { return new XNumber(s).numberValue(); });

        return numbers;
    }

    // [Context, Expression*] String (Number* -> Number) -> XNumber or String
    function mathAggr(args, name, oper) {
        const c = args[0];
        const expr = args[1];
        let nodes;

        if (expr) {
            nodes = expr.evaluate(c);
        }

        if (args.length !== 2 || !Utilities.instance_of(nodes, XNodeSet)) {
            argError(name, "node-set");
        }

        nodes = getNonBlankNumbers(nodes.toUnsortedArray());

        if (nodes.length) {
            var result = oper.apply(null, nodes);
            return new XNumber(result);
        }

        return new XString("");
    }

    // [Number*] -> Number
    // Produce the average of all numbers in arguments
    function average() {
        var sum = Array.prototype.reduce.call(arguments, function (n1, n2) { return n1 + n2; }, 0);
        return sum / arguments.length;
    }

    // Context Expression* -> XNumber or XString
    function avg() { return mathAggr(arguments, "Avg", average); }
    function min() { return mathAggr(arguments, "Min", Math.min); }
    function max() { return mathAggr(arguments, "Max", Math.max); }

    function getNamedNodeProperty(c) {
        const params = getParams(c, arguments);

        if (arguments.length !== 4 || !Utilities.instance_of(params[0], XNodeSet)) {
            argError("GetNamedNodeProperty", "node-set, string, string");
        }

        const first = params[0].first();
        const key = params[1].toString();
        const def = params[2].toString();

        const retVal = getNodePropertyImpl(first, key, def);

        return new XString(retVal);
    }

    function getNodePropertyImpl(node, key, defaultValue) {
        var val, retVal;

        if (node) {
            val = getNodeData(node, key);
            retVal = (typeof val === "undefined") ? defaultValue : val.toString();
        } else {
            retVal = defaultValue;
        }

        return retVal;
    }

    function getDom(c) {
        var ns;

        if (arguments.length !== 1) {
            argError("get-DOM", "(none)");
        }

        ns = new XNodeSet();
        ns.add(dataSources.getDom().getNode());

        return ns;
    }

    function getSecondaryDom(c, pName) {
        var ns, sName, ds, dom, domAr;

        if (arguments.length !== 2) {
            argError("GetDOM", "string");
        }

        pName = getParam(c, pName);

        sName = pName.toString();
        ds = dataSources.getDom(sName);

        dom = ds && ds.getNode();
        domAr = dom ? [dom] : [];

        ns = new XNodeSet();
        ns.addArray(domAr);

        return ns;
    }

    function functionAvailable(c, params) {
        if (params.length !== 1) {
            argError("function-available", "string");
        }

        const functionName = params[0].stringValue();
        const func = FunctionResolver.getFunctionFromContext(functionName, c);

        return new XBoolean(func);
    }

    function current(c, params) {
        if (params.length !== 0) {
            argError('current', '');
        }

        var ns = new XNodeSet();

        ns.add(c.expressionContextNode);

        return ns;
    }

    function assembleId(ds, id) {
        if (!id) {
            return '';
        }
        if (!ds) {
            return id;
        }
        return ds + ":" + id;
    }

    function generateId(c, params) {
        if (params.length > 1 ||
            (params.length === 1 && !Utilities.instance_of(params[0], XNodeSet))) {
            argError("generate-id", "[node-set]");
        }

        var node = params.length === 1 ? params[0].first() : c.contextNode;

        var ds = getNodePropertyImpl(node, "fvDs", "");
        var id = getNodePropertyImpl(node, "fvUid", "");

        return new XString(assembleId(ds, id));
    }

    function getUserName() {
        return new XString(userName || '');
    }


    // ======
    // XdDate
    // ======

    function today() {
        var dt = new Date();

        return new XString(getXmlDate(dt));
    }

    function now() {
        var dt = new Date();

        return new XString(getXmlDate(dt) + "T" + getXmlTime(dt));
    }

    function addTime(date, units, omitTimeIfZero, component) {
        var dateStr = date.stringValue();
        var unitsStr = units.stringValue();

        if (dateStr === '' || unitsStr === '') {
            return emptyXString;
        }

        var dateSpec = parseIsoDate(dateStr);
        var unitsNum = Number(unitsStr);

        if (!dateSpec || Number.isNaN(unitsNum) || Math.floor(unitsNum) !== unitsNum) {
            return new XString("ERR?");
        }

        //Case 50273 - AddDays - sometimes adds an additional hour
        dateSpec.date['setUTC' + component](
            dateSpec.date['getUTC' + component]() + unitsNum
        );
        dateSpec.omitTimeIfZero = omitTimeIfZero;
        dateSpec.omitMilliseconds = true;

        return makeIsoDate(dateSpec);
    }

    function addDays(c, params) {
        var date = params[0],
            days = params[1];

        if (params.length !== 2) {
            argError("AddDays", "string, number");
        }

        var newDate = addTime(date, days, true, 'Date');

        return new XString(newDate);
    }

    function addSeconds(c, params) {
        var date = params[0],
            seconds = params[1];

        if (params.length !== 2) {
            argError('AddSeconds', 'string, number');
        }

        var newDate = addTime(date, seconds, false, 'Seconds');

        return new XString(newDate);
    }

    // ==========
    // END XdDate
    // ==========

    // ============
    // XdFormatting
    // ============

    function formatDate(value, options) {
        // 2017-02-21 - Skipping current functionality because it causes date to be displayed a day off
        //              in certain timezones (case 42803)
        try {
            return dateFormatter.formatDate(value, options);
        } catch (e) {
            console.error(e);
            return value;
        }
    }

    function needsRounding(numDigits) {
        return typeof numDigits !== "undefined" && numDigits !== 'auto';
    }

    function formatDigits(num, numDigits) {
        return needsRounding(numDigits)
            ? num.toFixed(numDigits)
            : num.toString();
    }

    function appendZero(number, appendCount) {
        return number + Array(appendCount + 1).join("0");
    }

    function appendFractionalValue(fractionalValue, numDigits) {
        if (!needsRounding(numDigits)) {
            return '.' + fractionalValue;
        }

        var intNumDigits = parseInt(numDigits);

        var digitLength = fractionalValue.length < intNumDigits ? fractionalValue.length : intNumDigits,
            remainingDigit = intNumDigits - fractionalValue.length,
            formattedValue = "." + fractionalValue.substr(0, digitLength);

        if (remainingDigit === 0) {
            return formattedValue;
        }

        return appendZero(formattedValue, remainingDigit);
    }

    function formatGrouping(numberString, numDigits, grouping) {
        //If grouping is not specified, options has grouping = 0
        if (!grouping) {
            var splitNumberString = numberString.split('.'),
                decFormattedNumber = Number(splitNumberString[0]);

            if (Number.isNaN(decFormattedNumber)) {
                return numberString;
            }

            var formattedNumber = decFormattedNumber.toLocaleString('en-us'),
                splitFormattedNumber = formattedNumber.split('.'),
                integerValue = splitFormattedNumber[0];//Split and use integer value, as IE appends decimal to formattedNumber

            if (splitNumberString.length > 1) {
                integerValue += appendFractionalValue(splitNumberString[1], numDigits);
            }

            return integerValue;
        }

        return numberString;
    }

    function formatNumber(value, type, options) {
        var num = parseFloat(value);

        if (Number.isNaN(num) || Number.isNaN(Number(value))) {
            return value;
        }

        var decFormatted = formatGrouping(formatDigits(num, options.numDigits), options.numDigits, options.grouping);

        // TODO: handle other options

        return decFormatted;
    }

    function formatCurrency(value, options) {
        var num = parseFloat(value);

        if (Number.isNaN(num) || Number.isNaN(Number(value))) {
            return value;
        }

        var currencyLocale = currencyLocales[options['currencyLocale']];
        if (!currencyLocale) {
            return num.toString();
        }

        var formattedValue = formatGrouping(formatDigits(num, options.numDigits), options.numDigits, options.grouping);

        return currencyLocale.position === 'before'
            ? currencyLocale.symbol + formattedValue
            : formattedValue + currencyLocale.symbol;
    }

    function formatPercentage(value, options) {
        var num = parseFloat(value);

        if (Number.isNaN(num)) {
            return value;
        }

        var factorValue = value * 100;

        return formatDigits(factorValue, options.numDigits);
    }

    function formatStringInner(value, type, options) {
        switch (type) {
            case "date":
            case "datetime":
                return formatDate(value, options);
            case "number":
                return formatNumber(value, type, options);
            case "currency":
                return formatCurrency(value, options);
            case "percentage":
                return formatPercentage(value, options);
        }

        return value;

    }

    function splitOptions(options) {
        var parts = options.split(";").filter(function (p) {
            return p;
        }),
            partsSubdiv = parts.map(function (p) {
                return p.split(":", 2);
            }),
            result = {};

        partsSubdiv.forEach(function (el) {
            result[el[0]] = el[1] || null;
        });

        return result;
    }

    function formatString(c) {
        const params = getParams(c, arguments);

        if (params.length !== 3) {
            argError("formatString", "string, string, string");
        }

        const value = params[0].stringValue();
        const type = params[1].stringValue();
        const options = splitOptions(params[2].stringValue());

        return new XString(formatStringInner(value, type, options));
    }

    // ================
    // END XdFormatting
    // ================

    // ======
    // XdEnvironment
    // ======

    const isBrowser = () => falseXBoolean;

    // ======
    // End XdEnvironment
    // ======

    // ==============
    // Msxsl
    // ==============

    function stringCompare(c) {
        var params = getParams(c, arguments),
            x, y, language, options,
            res;

        if (params.length < 2 || params.length > 4) {
            argError("string-compare", "string, string [, string [, string]]");
        }

        x = params[0];
        y = params[1];
        language = params[2];
        options = params[3] || "";

        if (options === "i") {
            x = x.toLowerCase();
            y = y.toLowerCase();
        }
        // TODO: Improve this behavior, add support for u

        if (x < y) {
            res = -1;
        }
        else if (x > y) {
            res = 1;
        }
        else {
            res = 0;
        }

        return new XNumber(res);
    }


    function tryProcessRegex(pattern) {
        try {
            // case 48368, 45620 - Touch up pattern to bring it more in line with XSD regex
            // For now: replace $ with \$ and surround pattern with ^...$
            var adjustedPattern = '^' + pattern.replace(/\$/g, '\\$') + '$';

            return new RegExp(adjustedPattern);
        } catch (e) {
            throw new Error("Unsupported regex pattern: " + pattern + "  Error: " + e.message);
        }
    }

    function match(c, params) {
        var value = params[0];
        var pattern = params[1];

        if (params.length !== 2) {
            argError("Match", "string, string");
        }

        var regex = tryProcessRegex(pattern.stringValue());

        return new XBoolean(regex.test(value.stringValue()));
    }

    function documentFunc(c, params) {
        if (params.length !== 1) {
            argError('document', 'string');
        }

        // TODO?: support returning multiple documents?
        var dsname = params[0].stringValue();

        var ds = dataSources.getDom(dsname);

        var domAr = ds ? [ds.getNode()] : [];

        var ns = new XNodeSet();
        ns.addArray(domAr);

        // TODO?: search files if DOM not present?
        return ns;
    }

    // Case 40939
    function getMajorVersion() {
        // IP 2013 is version 15.x.x.x
        return new XNumber(15);
    }

    const xsltFunc = (f) => (...args) =>
        f(args[0], getParams(args[0], args))

    const addFunc = (namespace, name, func) => {
        self.addFunction(namespace, name, xsltFunc(func));
    }

    const addFunctions = (namespace, functionMap) => {
        for (const [name, fn] of Object.entries(functionMap)) {
            addFunc(namespace, name, fn);
        }
    };

    this.addFunction(namespaceXdMath, "Nz", nz);
    this.addFunction(namespaceXdMath, "Avg", avg);
    this.addFunction(namespaceXdMath, "Max", max);
    this.addFunction(namespaceXdMath, "Min", min);
    this.addFunction(namespaceXdMath, "Eval", xdEval);

    this.addFunction(namespaceXdXDocument, "GetNamedNodeProperty", getNamedNodeProperty);
    this.addFunction(namespaceXdXDocument, "GetDOM", getSecondaryDom);
    this.addFunction(namespaceXdXDocument, "get-DOM", getDom);

    addFunc("", "function-available", functionAvailable);
    addFunc("", "generate-id", generateId);
    addFunc('', 'current', current);
    addFunc('', 'document', documentFunc);

    this.addFunction(namespaceXdUser, "get-UserName", getUserName);

    this.addFunction(namespaceXdDate, "Today", today);
    this.addFunction(namespaceXdDate, "Now", now);
    addFunc(namespaceXdDate, "AddDays", addDays);
    addFunc(namespaceXdDate, "AddSeconds", addSeconds);

    this.addFunction(namespaceXdFormatting, "formatString", formatString);

    this.addFunction(nsMsxsl, "string-compare", stringCompare);

    addFunc(namespaceXdUtil, "Match", match);

    addFunctions(namespaceFv, makeFvXsltFunctions(characterLimitSource, viewManager, settings));

    addFunc(namespaceIpApp, 'GetMajorVersion', getMajorVersion);

    addFunc(namespaceXdEnvironment, "IsBrowser", isBrowser);
}

inherit(FVFunctions, XPathFunctionBase);


function msFunctions() {
    const self = this;

    function nodeSet(c, pArg) {
        var arg = getParam(c, pArg);

        if (Utilities.instance_of(arg, XNodeSet)) {
            return arg;
        }

        return makeSingleValueNodeSet(arg.stringValue());
    }

    self.addFunction(nsMsxsl, "node-set", nodeSet);
}

inherit(msFunctions, XPathFunctionBase);

const allFunctions = (
    dataSources,
    user,
    characterLimitSource,
    viewManager,
    settings,
) => {
    const ms = new msFunctions();
    const fv = new FVFunctions(dataSources, user, characterLimitSource, viewManager, settings);

    return {
        getFunction: function (name, context) {
            return fv.getFunction(name, context) || ms.getFunction(name, context);
        },
    };
}

export {
    msFunctions,
    allFunctions,
};
